import { useEffect, useRef, useState } from 'react'
import {
    ArrowRightIcon,
    Building2Icon,
    CompassIcon,
    LightbulbIcon,
    MessageCircleHeartIcon,
    PlusIcon,
    SearchIcon,
    ShieldCheckIcon,
} from 'lucide-react'
import { observer } from 'mobx-react-lite'
import { useTranslation } from 'react-i18next'

import { env } from '@campai/lib-web/env'
import { Actor, ActorName, useUserActor } from '@campai/lib-web/components/actor'
import { OrganizationAvatar } from '@campai/lib-web/components/OrganizationAvatar'
import { DEMO_ORGANIZATION_ID } from '@campai/lib-common/entities'
import { formatAddressLine } from '@campai/lib-common/utils/address'
import { getProductProfile } from '@campai/lib-common/utils/misc'
import { asObjectId } from '@campai/lib-common/utils/objectid'
import { hasAdminOrganizationAccess } from '@campai/lib-common/utils/organization'
import { Markdown } from '@campai/lib-web/components'
import {
    Button,
    Card,
    CardContent,
    CardDescription,
    CardFooter,
    CardHeader,
    CardTitle,
    InputGroup,
    InputGroupAddon,
    InputGroupInput,
    Link,
    Skeleton,
} from '@campai/lib-web/components/new/ui'
import { RouterOutput, client } from '@campai/lib-web/utils/trpc'

import { appState } from '@/appState'
import { Page } from '@/components/Page'
import { canReadCommunity } from '@/product/community/access'
import { canReadFeedback } from '@/product/feedback/access'

type OrganizationListItem = RouterOutput['currentUser']['listOrganizations']['organizations'][number]

const ORGANIZATION_PAGE_SIZE = 20
const canCreateOrganization = getProductProfile(env.VITE_PRODUCT_KEY).organization.canCreate
const communityIconClassName =
    'border-violet-200 bg-violet-50 text-violet-700 dark:border-violet-900 dark:bg-violet-950/30 dark:text-violet-300'
const feedbackIconClassName =
    'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-300'

export const Home = observer(() => {
    const { i18n, t } = useTranslation()
    const [organizations, setOrganizations] = useState<OrganizationListItem[] | null>(null)
    const [search, setSearch] = useState('')
    const [submittedSearch, setSubmittedSearch] = useState('')
    const [organizationCount, setOrganizationCount] = useState(0)
    const [isLoading, setIsLoading] = useState(true)
    const [isLoadingMore, setIsLoadingMore] = useState(false)
    const requestVersionRef = useRef(0)
    const actor = useUserActor(appState.user)
    const hasSystemAccess =
        appState.hasSystemPermission('organization.read') ||
        appState.hasSystemPermission('organization.billing') ||
        appState.hasSystemPermission('user.read') ||
        appState.hasSystemPermission('auditLog.read')
    const hasCommunityAccess = canReadCommunity()
    const hasFeedbackAccess = canReadFeedback()

    useEffect(() => {
        const abortController = new AbortController()
        const requestVersion = requestVersionRef.current + 1
        requestVersionRef.current = requestVersion
        const searchTerm = submittedSearch.trim()

        setIsLoading(true)
        setIsLoadingMore(false)
        setOrganizations(null)
        setOrganizationCount(0)

        void client.currentUser.listOrganizations
            .query(
                {
                    limit: ORGANIZATION_PAGE_SIZE,
                    offset: 0,
                    returnCount: true,
                    searchTerm: searchTerm || undefined,
                },
                { signal: abortController.signal }
            )
            .then((result) => {
                if (requestVersionRef.current !== requestVersion) {
                    return
                }

                setOrganizations(result.organizations)
                setOrganizationCount(result.count)
            })
            .catch(() => {
                if (abortController.signal.aborted || requestVersionRef.current !== requestVersion) {
                    return
                }

                setOrganizations([])
                setOrganizationCount(0)
            })
            .finally(() => {
                if (abortController.signal.aborted || requestVersionRef.current !== requestVersion) {
                    return
                }

                setIsLoading(false)
            })

        return () => {
            abortController.abort()
        }
    }, [submittedSearch])

    const i18nKey = 'home'
    const searchTerm = submittedSearch.trim()
    const canLoadMore = !!organizations && organizations.length < organizationCount
    const showEmptyOrganizationChoices = !isLoading && organizations?.length === 0 && !searchTerm

    const submitSearch = () => {
        setSubmittedSearch(search.trim())
    }

    const loadMoreOrganizations = () => {
        if (!organizations || !canLoadMore || isLoadingMore) {
            return
        }

        if (search.trim() !== searchTerm) {
            submitSearch()
            return
        }

        const requestVersion = requestVersionRef.current

        setIsLoadingMore(true)

        void client.currentUser.listOrganizations
            .query({
                limit: ORGANIZATION_PAGE_SIZE,
                offset: organizations.length,
                returnCount: true,
                searchTerm: searchTerm || undefined,
            })
            .then((result) => {
                if (requestVersionRef.current !== requestVersion) {
                    return
                }

                setOrganizations((currentOrganizations) => [...(currentOrganizations || []), ...result.organizations])
                setOrganizationCount(result.count)
            })
            .catch(() => undefined)
            .finally(() => {
                if (requestVersionRef.current !== requestVersion) {
                    return
                }

                setIsLoadingMore(false)
            })
    }

    const getOrganizationUrl = (organizationId: string, shortId: string) => {
        const hasAdminAccess = hasAdminOrganizationAccess({
            organizationId: asObjectId(organizationId),
            user: appState.user,
        })

        const baseUrl = hasAdminAccess ? env.VITE_PRODUCT_URL_ADMIN : env.VITE_PRODUCT_URL_PORTAL

        return `${baseUrl}/${shortId}`
    }

    const getOrganizationSubtitle = (organization: OrganizationListItem) => {
        const address = formatAddressLine(organization.address, undefined, i18n.language)
        const details = [address, organization.website].filter((detail) => !!detail)

        return details.length ? details.join(' • ') : organization.shortId
    }

    return (
        <Page
            className="flex min-h-full flex-col space-y-6 lg:h-full lg:min-h-0"
            title={t(`${i18nKey}.title`)}
            description={t(`${i18nKey}.intro`, { productName: env.VITE_PRODUCT_NAME })}
        >
            <div className="grid gap-6 lg:min-h-0 lg:flex-1 lg:grid-cols-[minmax(0,1fr)_20rem]">
                <Card className="border-black/5 bg-white/90 lg:h-full lg:min-h-0 dark:border-white/10 dark:bg-neutral-950/70">
                    <CardHeader className="flex flex-col gap-4">
                        <div className="space-y-1">
                            <CardTitle>{t(`${i18nKey}.organizations`)}</CardTitle>
                            {!showEmptyOrganizationChoices && (
                                <CardDescription>{t(`${i18nKey}.description`)}</CardDescription>
                            )}
                        </div>
                        {!showEmptyOrganizationChoices && (
                            <div className="flex w-full flex-col gap-3 sm:flex-row">
                                <InputGroup className="min-w-0 max-w-none flex-1">
                                    <InputGroupInput
                                        value={search}
                                        onChange={(event) => setSearch(event.target.value)}
                                        onBlur={submitSearch}
                                        onKeyDown={(event) => {
                                            if (event.key === 'Enter') {
                                                event.currentTarget.blur()
                                            }
                                        }}
                                        placeholder={t(`${i18nKey}.searchPlaceholder`)}
                                    />
                                    <InputGroupAddon>
                                        <SearchIcon className="size-4" />
                                    </InputGroupAddon>
                                </InputGroup>
                                {canCreateOrganization && (
                                    <Button
                                        className="shrink-0"
                                        variant="secondary"
                                        nativeButton={false}
                                        render={<Link variant="unstyled" to="/create-organization" active={false} />}
                                    >
                                        <PlusIcon data-icon="inline-start" />
                                        {t(`${i18nKey}.createAction`)}
                                    </Button>
                                )}
                            </div>
                        )}
                    </CardHeader>
                    <CardContent className="lg:-mr-6 lg:min-h-0 lg:flex-1 lg:overflow-y-auto lg:pl-6 lg:pr-0">
                        <div className="space-y-3 lg:pr-12">
                            {isLoading &&
                                Array.from({ length: 3 }).map((_, index) => (
                                    <div
                                        key={`org-skeleton-${index.toString()}`}
                                        className="flex items-center gap-3 rounded-xl border p-4"
                                    >
                                        <Skeleton className="size-12 rounded-xl" />
                                        <div className="flex-1 space-y-2">
                                            <Skeleton className="h-4 w-1/3" />
                                            <Skeleton className="h-3 w-1/4" />
                                        </div>
                                    </div>
                                ))}
                            {showEmptyOrganizationChoices && (
                                <div className="grid gap-4 md:grid-cols-2">
                                    {canCreateOrganization && (
                                        <Link
                                            variant="unstyled"
                                            to="/create-organization"
                                            active={false}
                                            className="flex flex-col items-center rounded-xl border border-black/10 bg-white p-6 text-center shadow-sm transition-colors hover:border-black/20 hover:bg-black/[0.02] dark:border-white/10 dark:bg-neutral-900/80 dark:hover:border-white/20 dark:hover:bg-white/[0.03]"
                                        >
                                            <div className="flex size-20 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300">
                                                <Building2Icon className="size-10" />
                                            </div>
                                            <div className="mt-5 text-xl font-semibold">
                                                {t(`${i18nKey}.empty.title`)}
                                            </div>
                                            <div className="text-muted-foreground mt-2 text-sm leading-6">
                                                {t(`${i18nKey}.empty.description`, {
                                                    productName: env.VITE_PRODUCT_NAME,
                                                })}
                                            </div>
                                        </Link>
                                    )}
                                    <Link
                                        variant="unstyled"
                                        href={`${env.VITE_PRODUCT_URL_ADMIN}/${DEMO_ORGANIZATION_ID.toString()}`}
                                        active={false}
                                        className="flex flex-col items-center rounded-xl border border-black/10 bg-white p-6 text-center shadow-sm transition-colors hover:border-black/20 hover:bg-black/[0.02] dark:border-white/10 dark:bg-neutral-900/80 dark:hover:border-white/20 dark:hover:bg-white/[0.03]"
                                        target="_blank"
                                    >
                                        <div className="flex size-20 items-center justify-center rounded-2xl bg-sky-50 text-sky-700 dark:bg-sky-950/40 dark:text-sky-300">
                                            <CompassIcon className="size-10" />
                                        </div>
                                        <div className="mt-5 text-xl font-semibold">{t(`${i18nKey}.demo.title`)}</div>
                                        <div className="text-muted-fore ground mt-2 text-sm leading-6">
                                            {t(`${i18nKey}.demo.description`, {
                                                productName: env.VITE_PRODUCT_NAME,
                                            })}
                                        </div>
                                    </Link>
                                </div>
                            )}
                            {!isLoading && organizations?.length === 0 && searchTerm && (
                                <div className="rounded-xl border border-dashed p-6 text-center">
                                    <div className="mx-auto mb-3 flex size-12 items-center justify-center rounded-full bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300">
                                        <SearchIcon className="size-5" />
                                    </div>
                                    <div className="font-medium">{t(`${i18nKey}.searchEmpty.title`)}</div>
                                    <div className="text-muted-foreground mt-2 text-sm">
                                        {t(`${i18nKey}.searchEmpty.description`)}
                                    </div>
                                </div>
                            )}
                            {!isLoading &&
                                organizations?.map((organization) => {
                                    return (
                                        <Link
                                            variant="unstyled"
                                            key={organization._id.toString()}
                                            href={getOrganizationUrl(organization._id.toString(), organization.shortId)}
                                            active={false}
                                            className="group flex items-center gap-3 rounded-xl border border-black/5 bg-white p-4 transition-colors hover:border-black/10 hover:bg-black/[0.02] dark:border-white/10 dark:bg-neutral-900/80 dark:hover:border-white/20 dark:hover:bg-white/[0.03]"
                                        >
                                            <OrganizationAvatar
                                                name={organization.name}
                                                avatar={organization.avatar}
                                                className="size-12"
                                            />
                                            <div className="min-w-0 flex-1">
                                                <div className="truncate text-base font-medium">
                                                    {organization.name}
                                                </div>
                                                <div className="text-muted-foreground truncate text-sm">
                                                    {getOrganizationSubtitle(organization)}
                                                </div>
                                            </div>
                                            <ArrowRightIcon className="text-muted-foreground size-5 shrink-0 transition-transform group-hover:translate-x-0.5" />
                                        </Link>
                                    )
                                })}
                            {!isLoading && canLoadMore && (
                                <div className="flex justify-center pt-1">
                                    <Button variant="outline" loading={isLoadingMore} onClick={loadMoreOrganizations}>
                                        {t(`${i18nKey}.loadMore`)}
                                    </Button>
                                </div>
                            )}
                        </div>
                    </CardContent>
                </Card>
                <div className="space-y-6 lg:self-start">
                    {hasCommunityAccess && (
                        <Card size="sm" className="border-border/80 dark:bg-background/70 bg-white/90">
                            <CardHeader>
                                <div className="flex items-start gap-4">
                                    <div
                                        className={`flex size-11 shrink-0 items-center justify-center rounded-2xl border ${communityIconClassName}`}
                                    >
                                        <MessageCircleHeartIcon className="size-5" />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <CardTitle>{t(`${i18nKey}.community.title`)}</CardTitle>
                                        <CardDescription>{t(`${i18nKey}.community.description`)}</CardDescription>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardFooter>
                                <Button
                                    className="w-full"
                                    variant="secondary"
                                    nativeButton={false}
                                    render={<Link variant="unstyled" to="/product/community" active={false} />}
                                >
                                    {t(`${i18nKey}.community.action`)}
                                </Button>
                            </CardFooter>
                        </Card>
                    )}
                    {hasFeedbackAccess && (
                        <Card size="sm" className="border-border/80 dark:bg-background/70 bg-white/90">
                            <CardHeader>
                                <div className="flex items-start gap-4">
                                    <div
                                        className={`flex size-11 shrink-0 items-center justify-center rounded-2xl border ${feedbackIconClassName}`}
                                    >
                                        <LightbulbIcon className="size-5" />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <CardTitle>{t(`${i18nKey}.feedback.title`)}</CardTitle>
                                        <CardDescription>{t(`${i18nKey}.feedback.description`)}</CardDescription>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardFooter>
                                <Button
                                    className="w-full"
                                    variant="secondary"
                                    nativeButton={false}
                                    render={<Link variant="unstyled" to="/product/feedback" active={false} />}
                                >
                                    {t(`${i18nKey}.feedback.action`)}
                                </Button>
                            </CardFooter>
                        </Card>
                    )}
                    {hasSystemAccess && (
                        <Card
                            size="sm"
                            className="border-black/5 bg-white/90 dark:border-white/10 dark:bg-neutral-950/70"
                        >
                            <CardHeader>
                                <div className="flex items-start gap-4">
                                    <div className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-[#800000]/10 text-[#800000] dark:bg-[#800000]/30 dark:text-[#ffb3b3]">
                                        <ShieldCheckIcon className="size-5" />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <CardTitle>{t(`${i18nKey}.system.title`)}</CardTitle>
                                        <CardDescription>{t(`${i18nKey}.system.description`)}</CardDescription>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardFooter>
                                <Button
                                    className="w-full"
                                    variant="secondary"
                                    nativeButton={false}
                                    render={
                                        <Link variant="unstyled" href={env.VITE_PRODUCT_URL_SYSTEM} active={false} />
                                    }
                                >
                                    {t(`${i18nKey}.system.action`)}
                                </Button>
                            </CardFooter>
                        </Card>
                    )}
                    <Card size="sm" className="border-black/5 bg-white/90 dark:border-white/10 dark:bg-neutral-950/70">
                        <CardHeader>
                            <div className="flex items-center gap-4">
                                <Actor actor={actor} size="default" className="size-16" />
                                <div className="min-w-0 flex-1">
                                    <CardTitle className="truncate text-xl">
                                        <ActorName actor={actor} showCurrentUserAsYou={false} />
                                    </CardTitle>
                                    <CardDescription className="mt-1 truncate text-sm">
                                        {appState.user.email}
                                    </CardDescription>
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent className="pb-3 pt-1 text-sm">
                            <Markdown className="text-muted-foreground text-sm leading-relaxed">
                                {appState.user.bio || t(`${i18nKey}.profileSummary.bioEmpty`)}
                            </Markdown>
                        </CardContent>
                        <CardFooter className="flex-col gap-2">
                            <Button
                                className="w-full"
                                variant="secondary"
                                nativeButton={false}
                                render={<Link variant="unstyled" to="/profile" active={false} />}
                            >
                                {t(`${i18nKey}.profileSummary.editAction`)}
                            </Button>
                            <Button variant="outline" className="w-full" onClick={() => appState.auth.logout()}>
                                {t(`${i18nKey}.profileSummary.logoutAction`)}
                            </Button>
                        </CardFooter>
                    </Card>
                </div>
            </div>
        </Page>
    )
})
